/**
 * src/pages/AssistantShiftRequest.tsx
 * アシスタント用：シフト希望提出ページ
 *
 * - 翌月を初期選択（25日に提出するため）
 * - 当日が25日ならバナーを表示
 * - カレンダー形式でクリックして希望日を選択
 * - 提出済みの場合は読み込んで再編集可
 * - 締切超過 or 月次締め後は編集不可（読み取り専用）
 */

import { useEffect, useState, useCallback } from 'react'
import { api } from '../api/client'
import { todayJst } from '../utils/jst'
import { useAuth } from '../components/AuthGuard'
import { useIsMobile } from '../hooks/useIsMobile'

// ----------------------------------------------------------------
// 定数 / ユーティリティ
// ----------------------------------------------------------------
const MONTH_KEY = 'mangaos_shift_req_month'
const DOW_LABEL = ['日', '月', '火', '水', '木', '金', '土']

function nextMonth(): string {
  const d = new Date()
  d.setMonth(d.getMonth() + 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function getSavedMonth(): string {
  try {
    const s = localStorage.getItem(MONTH_KEY)
    if (s && /^\d{4}-\d{2}$/.test(s)) return s
  } catch { /* ignore */ }
  return nextMonth()
}

function todayStr(): string {
  return todayJst()
}

function todayDay(): number {
  return Number(todayJst().slice(8, 10))
}

function getDow(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d).getDay()
}

// ----------------------------------------------------------------
// インタラクティブカレンダー（クリックで希望日を選択）
// ----------------------------------------------------------------
function ShiftRequestCalendar({
  month,
  selected,
  onToggle,
  disabled,
}: {
  month: string
  selected: Set<string>
  onToggle: (date: string) => void
  disabled: boolean
}) {
  const [y, m] = month.split('-').map(Number)
  const daysInMonth = new Date(y, m, 0).getDate()
  const today = todayStr()

  const allDays = Array.from({ length: daysInMonth }, (_, i) =>
    `${month}-${String(i + 1).padStart(2, '0')}`
  )

  // 月曜始まりのグリッド（空白 = null）
  const weeks: (string | null)[][] = []
  let week: (string | null)[] = []

  allDays.forEach((date, idx) => {
    if (idx === 0) {
      const dow = getDow(date)
      const offset = dow === 0 ? 6 : dow - 1  // Mon=0 ... Sun=6
      for (let i = 0; i < offset; i++) week.push(null)
    }
    week.push(date)
    // 日曜 or 月末で行を区切る
    if (getDow(date) === 0 || idx === allDays.length - 1) {
      while (week.length < 7) week.push(null)
      weeks.push(week)
      week = []
    }
  })

  return (
    <div>
      {/* 曜日ヘッダー（月〜日） */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
        {['月', '火', '水', '木', '金', '土', '日'].map((d, i) => (
          <div key={d} style={{
            textAlign: 'center', fontSize: 11, fontWeight: 600,
            color: i === 5 ? '#2980b9' : i === 6 ? '#c0392b' : 'var(--ink-3)',
            paddingBottom: 4,
          }}>
            {d}
          </div>
        ))}
      </div>

      {/* 日付チップ（週ごとの行） */}
      {weeks.map((weekRow, wi) => (
        <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
          {weekRow.map((date, di) => {
            if (!date) return <div key={`empty-${wi}-${di}`} />

            const dow        = getDow(date)
            const isSun      = dow === 0
            const isSat      = dow === 6
            const isSelected = selected.has(date)
            const isPast     = date < today
            const day        = Number(date.slice(8))

            let bg     = isSun ? '#fff5f5' : isSat ? '#f0f6ff' : 'var(--paper-2)'
            let color  = isSun ? '#c0392b' : isSat ? '#2980b9' : 'var(--ink)'
            let border = '1px solid transparent'

            if (isSelected) {
              bg     = 'var(--green)'
              color  = '#fff'
              border = '1px solid transparent'
            }

            return (
              <div
                key={date}
                onClick={() => !disabled && onToggle(date)}
                title={`${date}（${DOW_LABEL[dow]}）`}
                style={{
                  textAlign: 'center',
                  borderRadius: 6,
                  padding: '8px 2px',
                  background: bg,
                  color,
                  border,
                  fontSize: 14,
                  fontWeight: isSelected ? 700 : 400,
                  fontFamily: 'var(--font-mono)',
                  cursor: disabled ? 'default' : 'pointer',
                  opacity: isPast ? (isSelected ? 0.7 : 0.4) : 1,
                  transition: 'background 0.1s, color 0.1s',
                  userSelect: 'none',
                }}
              >
                {day}
              </div>
            )
          })}
        </div>
      ))}

      {/* 凡例 */}
      <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 11, color: 'var(--ink-3)', flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{
            width: 12, height: 12, borderRadius: 3,
            background: 'var(--green)', display: 'inline-block',
          }} />
          出勤希望
        </span>
        <span style={{ color: '#c0392b' }}>日 = 日曜</span>
        <span style={{ color: '#2980b9' }}>土 = 土曜</span>
        <span style={{ opacity: 0.45 }}>薄い日 = 過去日</span>
      </div>
    </div>
  )
}

// ----------------------------------------------------------------
// 選択済み日付リスト（読み取り専用表示用）
// ----------------------------------------------------------------
function SelectedDateChips({ dates }: { dates: string[] }) {
  if (dates.length === 0) {
    return <p style={{ color: 'var(--ink-3)', fontSize: 13 }}>希望日なし</p>
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {[...dates].sort().map((date) => {
        const dow  = getDow(date)
        const isSun = dow === 0
        const isSat = dow === 6
        return (
          <span key={date} style={{
            fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 500,
            background: 'var(--green-light)', color: 'var(--green)',
            borderRadius: 4, padding: '3px 8px',
            border: '1px solid rgba(45,122,58,0.2)',
          }}>
            {date.slice(5).replace('-', '/')}
            <span style={{
              fontSize: 10, marginLeft: 3,
              color: isSun ? '#c0392b' : isSat ? '#2980b9' : 'var(--green)',
            }}>
              （{DOW_LABEL[dow]}）
            </span>
          </span>
        )
      })}
    </div>
  )
}

// ----------------------------------------------------------------
// メインページ
// ----------------------------------------------------------------
export default function AssistantShiftRequest() {
  const { user }  = useAuth()
  const isMobile  = useIsMobile()

  const [month,           setMonthState]   = useState<string>(getSavedMonth)
  const [selectedDates,   setSelectedDates] = useState<Set<string>>(new Set())
  const [note,            setNote]          = useState('')
  const [loading,         setLoading]       = useState(true)
  const [saving,          setSaving]        = useState(false)
  const [existingRequest, setExistingRequest] = useState<any>(null)
  const [deadlineDate,    setDeadlineDate]  = useState<string | null>(null)
  const [deadlinePassed,  setDeadlinePassed] = useState(false)
  const [closed,          setClosed]        = useState(false)
  const [toast,           setToast]         = useState<{ msg: string; ok: boolean } | null>(null)

  // 25日バナー
  const isSubmitDay = todayDay() === 25

  const setMonth = (m: string) => {
    setMonthState(m)
    try { localStorage.setItem(MONTH_KEY, m) } catch { /* ignore */ }
  }

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok })
    setTimeout(() => setToast(null), 5000)
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api.my.shiftRequests.get(month)
      setDeadlineDate(data.deadline_date)
      setDeadlinePassed(data.deadline_passed)
      setClosed(data.closed)
      if (data.request) {
        setExistingRequest(data.request)
        const dates: string[] = JSON.parse(data.request.available_dates ?? '[]')
        setSelectedDates(new Set(dates))
        setNote(data.request.note ?? '')
      } else {
        setExistingRequest(null)
        setSelectedDates(new Set())
        setNote('')
      }
    } catch {
      // エラー時は空状態のまま
    } finally {
      setLoading(false)
    }
  }, [month])

  useEffect(() => { load() }, [load])

  const toggleDate = (date: string) => {
    setSelectedDates((prev) => {
      const next = new Set(prev)
      if (next.has(date)) next.delete(date)
      else next.add(date)
      return next
    })
  }

  const handleSubmit = async () => {
    setSaving(true)
    try {
      await api.my.shiftRequests.submit({
        target_month:    month,
        available_dates: [...selectedDates].sort(),
        note:            note.trim() || undefined,
      })
      showToast(existingRequest ? 'シフト希望を更新しました' : 'シフト希望を提出しました', true)
      await load()
    } catch (e: any) {
      showToast(e.message ?? '提出に失敗しました', false)
    } finally {
      setSaving(false)
    }
  }

  // 月次締め済みのみロック。締切超過は警告表示のみで操作は可能
  const isLocked       = closed
  const selectedCount  = selectedDates.size
  const selectedArray  = [...selectedDates].sort()

  return (
    <div>
      {/* ── トースト ── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: toast.ok ? '#e8f4e9' : '#fde8e6',
          border: `1px solid ${toast.ok ? 'rgba(45,122,58,0.3)' : 'rgba(192,57,43,0.25)'}`,
          borderRadius: 8, padding: '12px 20px', zIndex: 200,
          minWidth: 240, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: toast.ok ? 'var(--green)' : 'var(--red)' }}>
            {toast.ok ? '✓ ' : '⚠ '}{toast.msg}
          </span>
          <button onClick={() => setToast(null)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 16, color: 'var(--ink-3)', lineHeight: 1,
          }}>✕</button>
        </div>
      )}

      {/* ── 25日バナー ── */}
      {isSubmitDay && (
        <div style={{
          background: '#e6f0fb', border: '1px solid rgba(30,95,168,0.25)',
          borderRadius: 8, padding: '12px 16px', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 22, lineHeight: 1 }}>📅</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--blue)' }}>
              今日はシフト希望の提出日です！
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 3 }}>
              翌月分のシフト希望を忘れずに提出してください。
            </div>
          </div>
        </div>
      )}

      {/* ── 月選択 + ユーザー名 ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{ fontFamily: 'var(--font-mono)', fontSize: 14, width: 150 }}
        />
        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{user?.assistant_name ?? ''}</span>
        {!isLocked && (
          <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 'auto' }}>
            希望日をタップ/クリックして選択
          </span>
        )}
      </div>

      {/* ── 月次締めバナー（操作ロック） ── */}
      {closed && (
        <div style={{
          background: '#fde8e6', border: '1px solid rgba(192,57,43,0.25)',
          borderRadius: 8, padding: '10px 16px', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--red)', whiteSpace: 'nowrap' }}>
            🔒 月次締め済み
          </span>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            この月は締め処理が完了しているため提出・更新できません
          </span>
        </div>
      )}

      {/* ── 締切超過バナー（警告のみ・提出は可能） ── */}
      {!closed && deadlinePassed && deadlineDate && (
        <div style={{
          background: '#fef3dc', border: '1px solid rgba(176,122,18,0.25)',
          borderRadius: 8, padding: '10px 16px', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--amber)', whiteSpace: 'nowrap' }}>
            ⚠ 提出期限超過
          </span>
          <span style={{ fontSize: 13, color: 'var(--ink-2)', fontFamily: 'var(--font-mono)' }}>
            {deadlineDate}
          </span>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            — 期限を過ぎていますが、月次締め前であれば提出できます
          </span>
        </div>
      )}

      {/* ── 通常の締切日表示（期限前） ── */}
      {!closed && !deadlinePassed && deadlineDate && (
        <div style={{
          background: 'var(--paper-2)', border: '1px solid var(--paper-3)',
          borderRadius: 8, padding: '8px 14px', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            提出期限：
          </span>
          <span style={{ fontSize: 13, color: 'var(--ink-2)', fontFamily: 'var(--font-mono)', fontWeight: 500 }}>
            {deadlineDate}
          </span>
        </div>
      )}

      {loading ? (
        <div className="loading" style={{ padding: 48 }}>LOADING...</div>
      ) : (
        <>
          {/* ── 提出済みバッジ ── */}
          {existingRequest && (
            <div style={{
              background: 'var(--green-light)', border: '1px solid rgba(45,122,58,0.2)',
              borderRadius: 8, padding: '10px 16px', marginBottom: 16,
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            }}>
              <span style={{ color: 'var(--green)', fontWeight: 600, fontSize: 13 }}>✓ 提出済み</span>
              {existingRequest.submitted_at && (
                <span style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
                  {existingRequest.submitted_at.slice(0, 16).replace('T', ' ')}
                </span>
              )}
              {!isLocked && (
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                  — 期限内なら再提出で更新できます
                </span>
              )}
            </div>
          )}

          {/* ── カレンダーカード ── */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">
                {month} のシフト希望
                {selectedCount > 0 && (
                  <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 400, color: 'var(--green)' }}>
                    {selectedCount}日選択中
                  </span>
                )}
              </span>
              {!isLocked && selectedCount > 0 && (
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: 11, color: 'var(--ink-3)' }}
                  onClick={() => setSelectedDates(new Set())}
                >
                  リセット
                </button>
              )}
            </div>

            <div style={{ padding: isMobile ? '16px 14px' : '20px 24px' }}>
              {isLocked ? (
                /* 月次締め後: 読み取り専用 */
                <div>
                  <p style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 0, marginBottom: 12 }}>
                    月次締め済みのため、変更はできません。
                  </p>
                  <SelectedDateChips dates={selectedArray} />
                </div>
              ) : (
                /* 通常: インタラクティブカレンダー */
                <ShiftRequestCalendar
                  month={month}
                  selected={selectedDates}
                  onToggle={toggleDate}
                  disabled={isLocked}
                />
              )}
            </div>

            {/* メモ欄（編集可能時のみ） */}
            {!isLocked && (
              <div style={{ padding: isMobile ? '0 14px 18px' : '0 24px 20px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 6 }}>
                    備考・メモ（任意）
                  </label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="特記事項があれば入力してください（例：午後のみ、16時以降は不可 など）"
                    rows={3}
                    style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontSize: 13 }}
                  />
                </div>
              </div>
            )}

            {/* メモ（読み取り専用） */}
            {isLocked && existingRequest?.note && (
              <div style={{ padding: isMobile ? '0 14px 18px' : '0 24px 20px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 4 }}>備考</div>
                <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>{existingRequest.note}</div>
              </div>
            )}
          </div>

          {/* ── 提出ボタン ── */}
          {!isLocked && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
              {selectedCount === 0 && !existingRequest && (
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                  ※ 希望日を選択してください
                </span>
              )}
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={saving || selectedCount === 0}
                style={{ minWidth: 160 }}
              >
                {saving
                  ? '提出中...'
                  : existingRequest
                    ? '希望日を更新する'
                    : 'シフト希望を提出する'}
              </button>
            </div>
          )}

          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 10, paddingLeft: 2 }}>
            ※ 確定シフトは管理者が設定します。「確定シフト」メニューで確認できます。
            {!isLocked && ' 月次締め前であれば期限後でも再提出できます。'}
          </div>
        </>
      )}
    </div>
  )
}